import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { getHeaders } from '@utils/QueryClient/query-client.utils';
import { useTranslation } from '@utils/i18n/i18n.consumers';

function useUpdateStoryStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status, publishLinkedPlan = false }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-stories/${id}/${status}?publishLinkedPlan=${publishLinkedPlan}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!res.ok) {
        let errorData;
        try {
          errorData = await res.json();
        } catch (parseError) {
          throw new Error('Failed to update story status.');
        }

        if (errorData?.code === 'LINKED_PLAN_NOT_PUBLISHED') {
          throw new Error('Plan is not published.');
        } else {
          throw new Error('Failed to update story status.');
        }
      }

      return res.json();
    },
    onSuccess: (_, { status, publishLinkedPlan }) => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      // TODO: translation
      toast.success(`Story${publishLinkedPlan ? ' and linked plan' : ''} ${publishLinkedPlan ? 'have' : 'has'} been ${status === 'publish' ? 'published' : 'unpublished'}.`);
    },
    onError: (error) => {
      // TODO: translation
      if (error.message !== 'Plan is not published.') {
        toast.error(error.message);
      }
    },
  });
}

function useUpdatePlanStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-plans/${id}/${status}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!res.ok) throw new Error('Failed to update story status.');

      return res.json();
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // TODO: translation
      toast.success(`Plan has been ${status === 'publish' ? 'published' : 'unpublished'}.`);
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useDeleteStory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-stories/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!res.ok) throw new Error('Failed to delete story.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      // TODO: translation
      toast.success('Story has been deleted.');
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-plans/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!res.ok) throw new Error('Failed to delete plan.');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // TODO: translation
      toast.success('Plan has been deleted.');
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useUpdateAdaptationStory() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-stories/${id}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: data,
      });

      if (!res.ok) throw new Error('Failed to update adaptation story.');

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      // TODO: translation
      toast.success('Adaptation story has been updated.');
      navigate('/admin/stories');
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useCreatePlan() {
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-plans`, {
        method: 'POST',
        body: data,
      });

      if (!res.ok) throw new Error(t('Failed to create plan.'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      toast.success(t('Your plan will become visible after it is published by the administrator.'));
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

function useCreateStory() {
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-stories`, {
        method: 'POST',
        body: data,
      });

      if (!res.ok) throw new Error(t('Failed to create story.'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      toast.success(t('Your story will become visible after it is published by the administrator.'));
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

function useUpdateAdaptationPlan() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/adaptation-plans/${id}`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: data,
      });

      if (!res.ok) throw new Error('Failed to update adaptation plan.');

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      // TODO: translation
      toast.success('Adaptation plan has been updated.');
      navigate('/admin/plans');
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/users/${userId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!res.ok) {
        throw new Error('Failed to delete user.');
      }

      return null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // TODO: translation
      toast.success('User has been deleted successfully.');
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

function useUpdateUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, isApproved }) => {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/users/${userId}/access`, {
        method: 'PATCH',
        headers: {
          ...getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isApproved }),
      });

      if (!res.ok) {
        throw new Error('Failed to update user access.');
      }

      return null;
    },
    onSuccess: (_, { isApproved }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // TODO: translation
      toast.success(`User access has been ${isApproved ? 'approved' : 'removed'}.`);
    },
    onError: (error) => {
      // TODO: translation
      toast.error(error.message);
    },
  });
}

export {
  useCreatePlan,
  useDeletePlan,
  useDeleteStory,
  useDeleteUser,
  useUpdateAdaptationPlan,
  useUpdateAdaptationStory,
  useCreateStory,
  useUpdatePlanStatus,
  useUpdateStoryStatus,
  useUpdateUserAccess,
};